import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material"
import { grey } from "@mui/material/colors"
import { BoxFC } from "components/BoxCustom"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { SystemUpdateAltRounded } from "resource/muiImport"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { readTruckFixCostColumns } from "./columns"
import { truckFixCostPacer } from "./pacer"

// Gemini reads the files one by one on the server; keep each request small so it stays
// under multer's 20-file cap and the socket timeout, and so rows appear progressively.
const CHUNK_SIZE = 4

/**
 * อ่านเอกสารจากไฟล์ — drop / paste / pick ป้ายวงกลม, พ.ร.บ. and กรมธรรม์ scans, see what
 * was read off each one, then hand the whole lot to TruckFixCostDialog as a draft.
 *
 * ONE FILE IS ONE DOCUMENT. 26 ป้ายวงกลม paid on a single receipt come in as 26 files and
 * become 26 vehicle lines on one document; a fleet policy naming 42 ทะเบียน is one file
 * that becomes 42 lines. Each file is carried along so it can be attached to the vehicle
 * it belongs to once the document is saved.
 *
 * What Gemini read is a first guess, not an answer: the vehicle, เลขที่, period and amount
 * are all correctable in the grid before ใช้ข้อมูลนี้ hands them over.
 *
 * Nothing here writes to the database — ใช้ข้อมูลนี้ only fills the form behind it.
 */
const ReadTruckFixCostFileDialog = ({ dialogOpen, setDialogOpen, onApply }) => {

  const { ax, msData } = useContext(GlobalStateContext)
  // The drafts ARE the state — the grid rows are a flattened view of them, so an edit is
  // written back to the draft and the row follows. Handing back edited rows instead would
  // mean keeping two copies of the same document in step.
  const [drafts, setDrafts] = useState([])
  const [isReading, setIsReading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [processedCount, setProcessedCount] = useState(0)
  // seconds the pacer is holding this read back, 0 when it is not
  const [waitSec, setWaitSec] = useState(0)

  const readFiles = useCallback(async (fileList) => {
    const files = fileList.filter(f => f)
    if (files.length === 0) return
    setIsReading(true)
    setTotalCount(t => t + files.length)
    for (let i = 0; i < files.length; i += CHUNK_SIZE) {
      const chunk = files.slice(i, i + CHUNK_SIZE)
      // Reading and then saving is one long burst on one path, so the read spends from
      // the same minute-budget the uploads will need — see ./pacer.
      await truckFixCostPacer.take(setWaitSec)
      const formData = new FormData()
      chunk.forEach(f => formData.append("files", f))
      let data = null
      try {
        const value = await ax.post("/truck-fix-cost/readTruckFixCostFile", formData)
        data = value?.data
      } catch (err) {
        data = null   // network/timeout on this batch — mark its files and keep going
      }
      // The server answers in the order it was sent, so a draft belongs to the file at
      // the same index; a failed batch still produces one row per file.
      const batch = chunk.map((file, idx) => ({
        ...(data && data[idx] ? data[idx] : { FileNm: file.name, error: "อ่านไฟล์ไม่สำเร็จ", Items: [] }),
        file,
      }))
      setDrafts(o => [...o, ...batch])
      setProcessedCount(c => c + chunk.length)
    }
    setIsReading(false)
  }, [ax])

  // Trucks and trailers in one picker: the same list the document dialog resolves against,
  // prefixed because a TukId and a TlrId can both be 12.
  const vehCombo = useMemo(() => [
    ...(msData.truckCombo || []).map(t => ({ ...t, id: `T${t.id}`, group: "รถ" })),
    ...(msData.trailerCombo || []).map(t => ({ ...t, id: `L${t.id}`, group: "หางพ่วง" })),
  ], [msData.truckCombo, msData.trailerCombo])

  /** One line per vehicle; a file that could not be read keeps one line saying so. */
  const rows = useMemo(() => {
    const out = []
    drafts.forEach((d, dIdx) => {
      const items = d.Items || []
      const fileNm = d.FileNm || d.file?.name
      if (d.error || items.length === 0) {
        out.push({
          id: out.length + 1, No: out.length + 1, dIdx, iIdx: -1, FileNm: fileNm,
          error: d.error || "ไม่พบรถในเอกสาร", TypNm: d.TypNm, Matched: 0,
        })
        return
      }
      items.forEach((it, iIdx) => {
        out.push({
          id: out.length + 1, No: out.length + 1, dIdx, iIdx, FileNm: fileNm,
          TypNm: d.TypNm, TukFixCostTypId: d.TukFixCostTypId,
          StrDte: d.StrDte, EndDte: d.EndDte,
          LicNm: it.LicNm, Matched: it.Matched, DocNo: it.DocNo, Amnt: it.Amnt,
          VehKey: it.TukId ? `T${it.TukId}` : it.TlrId ? `L${it.TlrId}` : null,
        })
      })
    })
    return out
  }, [drafts])

  const updateDraft = useCallback((dIdx, fn) => {
    setDrafts(o => o.map((d, i) => i === dIdx ? fn(d) : d))
  }, [])

  const handleEditCommit = useCallback(({ id, field, value }) => {
    const row = rows.find(r => r.id === id)
    if (!row || row.iIdx < 0) return
    // StrDte/EndDte belong to the document, not to one of its vehicles.
    if (field === "StrDte" || field === "EndDte") {
      const dte = value ? dayjs(value).format("YYYY-MM-DD") : null
      updateDraft(row.dIdx, d => ({ ...d, [field]: dte }))
      return
    }
    updateDraft(row.dIdx, d => {
      const val = field === "Amnt" ? (value == null || value === "" ? null : +value) : value
      const Items = d.Items.map((it, i) => i === row.iIdx ? { ...it, [field]: val } : it)
      // A one-vehicle document's total IS that vehicle's amount, and it is the total
      // TruckFixCostDialog takes for the header when a single file was read.
      const Amnt = (field === "Amnt" && Items.length === 1) ? val : d.Amnt
      return { ...d, Items, Amnt }
    })
  }, [rows, updateDraft])

  const handlePickVehicle = useCallback((rowId, key) => {
    const row = rows.find(r => r.id === rowId)
    if (!row || row.iIdx < 0) return
    const opt = vehCombo.find(v => v.id === key)
    updateDraft(row.dIdx, d => ({
      ...d,
      Items: d.Items.map((it, i) => i === row.iIdx ? {
        ...it,
        TukId: key && key.startsWith("T") ? +key.slice(1) : null,
        TlrId: key && key.startsWith("L") ? +key.slice(1) : null,
        LicNm: opt ? opt.label : it.LicNm,
        Matched: key ? 1 : 0,
      } : it),
    }))
  }, [rows, updateDraft, vehCombo])

  const columns = useMemo(() => readTruckFixCostColumns({
    vehCombo, onPickVehicle: handlePickVehicle,
  }), [vehCombo, handlePickVehicle])

  const handleSelectFiles = useCallback((e) => {
    readFiles(Array.from(e.target.files))
    e.target.value = null
  }, [readFiles])

  const handleDragOver = useCallback((e) => { e.preventDefault() }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    const dropped = e.dataTransfer.items
      ? Array.from(e.dataTransfer.items).filter(i => i.kind === 'file').map(i => i.getAsFile())
      : Array.from(e.dataTransfer.files)
    readFiles(dropped)
  }, [readFiles])

  const handlePaste = useCallback((e) => {
    const images = Array.from(e.clipboardData.items)
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile())
    if (images.length > 0) readFiles(images)
  }, [readFiles])

  const handleClose = useCallback(() => setDialogOpen(false), [setDialogOpen])

  const handleDialogOk = useCallback(() => {
    // A file that could not be read has nothing to contribute to the document.
    onApply(drafts.filter(d => !d.error && (d.Items || []).length > 0))
    setDialogOpen(false)
  }, [drafts, onApply, setDialogOpen])

  useEffect(() => {
    if (dialogOpen) {
      document.addEventListener('paste', handlePaste)
    } else {
      setDrafts([])
      setTotalCount(0)
      setProcessedCount(0)
      setWaitSec(0)
    }
    return () => document.removeEventListener('paste', handlePaste)
  }, [dialogOpen, handlePaste])

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='lg'
    >
      <DialogHeader handleDialogClose={handleClose} title="อ่านเอกสารจากไฟล์" onDeleteClick={null} />
      <DialogContent>
        <BoxFC pt={2} gap={2}>
          <Box
            minHeight={130}
            border={`2px dashed ${grey[400]}`}
            borderRadius={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{ bgcolor: grey[50] }}
          >
            <SystemUpdateAltRounded sx={{ fontSize: 40, color: grey[500] }} />
            <Typography color="text.secondary">
              {waitSec > 0
                ? `ส่งครบจำนวนที่ระบบอนุญาตต่อนาทีแล้ว จะอ่านต่อในอีก ${waitSec} วินาที`
                : isReading
                  ? `กำลังอ่าน... ${processedCount}/${totalCount} ไฟล์`
                  : totalCount > 0 ? `อ่านแล้ว ${totalCount} ไฟล์` : "ลากไฟล์มาวางที่นี่ หรือ Ctrl+V วางรูปภาพ"}
            </Typography>
            <Button variant="outlined" component="label" size="small" disabled={isReading}>
              เลือกไฟล์
              <input type="file" hidden multiple accept="image/*,application/pdf" onChange={handleSelectFiles} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              1 ไฟล์ = 1 เอกสาร (ป้ายวงกลม / พ.ร.บ. / กรมธรรม์) — ตรวจสอบก่อนบันทึกเสมอ
            </Typography>
          </Box>
          <Box height={400}>
            <DataGridCellExpand
              hideFooter
              disableSelectionOnClick
              rows={rows}
              columns={columns}
              onCellEditCommit={handleEditCommit}
              isCellEditable={(params) => params.row.iIdx >= 0}
            />
          </Box>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="ใช้ข้อมูลนี้"
        handleDialogClose={handleClose}
        handleDialogOk={handleDialogOk} />
    </Dialog>
  )
}

export default React.memo(ReadTruckFixCostFileDialog)
